import React, { useState, useMemo, useRef, useEffect } from "react";
import NotarialEditor from "./NotarialEditor";
import {
  getNotarizationTemplate,
  insertAffiantTableWithData,
  insertSignatureBlockWithData,
} from "./NotarialHelper";

const NotarizationSection = React.memo(
  ({
    formData,
    ackType,
    handleNotarizationChange,
    handleNotaryInfoChange,
    handleAffiantChange,
    handleWitnessChange,
    addAffiant,
    removeAffiant,
    addWitness,
    removeWitness,
    setFormData,
  }) => {
    const editorRef = useRef(null);

    // Determine document type
    const isJuratRes = ackType === "sec-res";
    const hasInitializedContent = useRef(false);

    // Get default template using helper function
    const defaultTemplate = useMemo(() => {
      return getNotarizationTemplate(ackType, formData);
    }, [ackType, formData]);

    useEffect(() => {
      // Don't run if notarization section doesn't exist
      if (!formData.notarization) return;

      // Only initialize once when content is empty/missing and we haven't initialized before
      const hasContent = formData.notarization?.content?.trim();
      const hasTemplate = defaultTemplate?.trim();

      if (!hasContent && hasTemplate && !hasInitializedContent.current) {
        hasInitializedContent.current = true;
        setFormData((prev) => ({
          ...prev,
          notarization: {
            ...prev.notarization,
            content: defaultTemplate,
          },
        }));
      }
    }, [defaultTemplate, formData.notarization, setFormData]);

    useEffect(() => {
      hasInitializedContent.current = false;
    }, [ackType]);

    // Handle editor content change
    const handleContentChange = (data) => {
      setFormData((prev) => ({
        ...prev,
        notarization: {
          ...prev.notarization,
          content: data,
        },
      }));
    };

    // Handle editor ready
    const handleEditorReady = (editor) => {
      editorRef.current = editor;
    };

    const handleInsertAffiantTable = () => {
      insertAffiantTableWithData(
        editorRef.current,
        formData.notarization?.affiants
      );
    };

    const handleInsertSignatureBlock = () => {
      insertSignatureBlockWithData(
        editorRef.current,
        formData.notarization?.witnesses
      );
    };

    const handleRefreshTemplate = () => {
      if (
        window.confirm(
          "This will revert the editor content with the default template. Any unsaved changes will be lost. Continue?"
        )
      ) {
        const freshTemplate = getNotarizationTemplate(ackType, formData);
        setFormData((prev) => ({
          ...prev,
          notarization: {
            ...prev.notarization,
            content: freshTemplate,
          },
        }));

        // Force editor to update
        if (editorRef.current) {
          editorRef.current.setData(freshTemplate);
        }
      }
    };

    return (
      <div>
        <div className="border-b-2 border-tier-secondary pb-4"></div>

        <div className="m-4 space-y-4">
          <h2 className="text-md font-bold mb-4">
            {isJuratRes ? "Resolution & Notary Content" : "Notary Content"}
          </h2>

          {/* Metadata Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="notary-location"
                className="block text-xs font-medium mb-1"
              >
                City/Location
              </label>
              <input
                id="notary-location"
                type="text"
                value={formData.notarization?.location || ""}
                onChange={(e) =>
                  handleNotarizationChange("location", e.target.value)
                }
                className="w-full custom-input px-4 py-2 border rounded"
                placeholder="e.g., Cebu City"
              />
            </div>

            <div>
              <label
                htmlFor="notary-date"
                className="block text-xs font-medium mb-1"
              >
                Date
              </label>
              <input
                id="notary-date"
                type="date"
                value={formData.notarization?.date || ""}
                onChange={(e) =>
                  handleNotarizationChange("date", e.target.value)
                }
                className="w-full custom-input px-4 py-2 border rounded"
              />
            </div>
          </div>

          {/* Resolution Fields (for Secretarial Resolution) */}
          {isJuratRes && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="resolution-number"
                    className="block text-xs font-medium mb-1"
                  >
                    Resolution Number
                  </label>
                  <input
                    id="resolution-number"
                    type="text"
                    value={formData.notarization?.resolutionNumber || ""}
                    onChange={(e) =>
                      handleNotarizationChange(
                        "resolutionNumber",
                        e.target.value
                      )
                    }
                    className="w-full custom-input px-4 py-2 border rounded"
                    placeholder="e.g., 006"
                  />
                </div>

                <div>
                  <label
                    htmlFor="resolution-year"
                    className="block text-xs font-medium mb-1"
                  >
                    Series Year
                  </label>
                  <input
                    id="resolution-year"
                    type="text"
                    value={formData.notarization?.resolutionYear || ""}
                    onChange={(e) =>
                      handleNotarizationChange("resolutionYear", e.target.value)
                    }
                    className="w-full custom-input px-4 py-2 border rounded"
                    placeholder="e.g., 2025"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="resolution-title"
                  className="block text-xs font-medium mb-1"
                >
                  Resolution Title
                </label>
                <input
                  id="resolution-title"
                  type="text"
                  value={formData.notarization?.resolutionTitle || ""}
                  onChange={(e) =>
                    handleNotarizationChange("resolutionTitle", e.target.value)
                  }
                  className="w-full custom-input px-4 py-2 border rounded"
                  placeholder="Resolution Title"
                />
              </div>
            </>
          )}

          {/* Affiant/Witness Management */}
          <div className="space-y-4">
            {/* Affiants Section */}
            <div className="bg-white/5 dark:bg-white/10 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <h4 className="font-bold">Affiants Table</h4>
              </div>

              {formData.notarization?.affiants?.map((affiant, index) => (
                <div
                  key={index}
                  className="border-b border-gray-600 pb-2 mb-2 last:border-0"
                >
                  <div className="flex gap-2 mb-2">
                    <div className="flex-1">
                      <label
                        htmlFor="affiant-name"
                        className="block text-xs font-medium mb-1"
                      >
                        Fullname
                      </label>
                      <input
                        id="affiant-name"
                        type="text"
                        value={affiant.name || ""}
                        onChange={(e) =>
                          handleAffiantChange(index, "name", e.target.value)
                        }
                        placeholder="Name"
                        className="custom-input text-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor="affiant-id-type"
                        className="block text-xs font-medium mb-1"
                      >
                        ID Type
                      </label>
                      <input
                        id="affiant-id-type"
                        type="text"
                        value={affiant.idType || ""}
                        onChange={(e) =>
                          handleAffiantChange(index, "idType", e.target.value)
                        }
                        placeholder="ID Type"
                        className="custom-input text-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor="affiant-id-num"
                        className="block text-xs font-medium mb-1"
                      >
                        ID Number
                      </label>
                      <input
                        id="affiant-id-num"
                        type="text"
                        value={affiant.idNumber || ""}
                        onChange={(e) =>
                          handleAffiantChange(index, "idNumber", e.target.value)
                        }
                        placeholder="ID Number"
                        className="custom-input text-sm"
                      />
                    </div>

                    <div className="flex-1">
                      <label
                        htmlFor="affiant-expiry"
                        className="block text-xs font-medium mb-1"
                      >
                        Expiry Date
                      </label>
                      <input
                        id="affiant-expiry"
                        type="text"
                        value={affiant.expiryDate || ""}
                        onChange={(e) =>
                          handleAffiantChange(
                            index,
                            "expiryDate",
                            e.target.value
                          )
                        }
                        placeholder="Expiry Date"
                        className="custom-input text-sm"
                      />
                    </div>

                    <div className="flex-1">
                      <label
                        htmlFor="affiant-issue"
                        className="block text-xs font-medium mb-1"
                      >
                        Place of Issue
                      </label>
                      <input
                        id="affiant-issue"
                        type="text"
                        value={affiant.issueInfo || ""}
                        onChange={(e) =>
                          handleAffiantChange(
                            index,
                            "issueInfo",
                            e.target.value
                          )
                        }
                        placeholder="Place of Issue"
                        className="custom-input text-sm"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => removeAffiant(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex gap-2 justify-end items-center mt-2">
                <button
                  type="button"
                  onClick={handleInsertAffiantTable}
                  className="btn-action-white text-sm"
                >
                  Insert Affiant Table into Editor
                </button>
                <button
                  type="button"
                  onClick={addAffiant}
                  className="btn-action text-sm"
                >
                  + Add Affiant
                </button>
              </div>
            </div>

            {/* Witnesses Section */}
            <div className="bg-white/5 dark:bg-white/10 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <h4 className="font-bold">Signatures</h4>
              </div>

              {formData.notarization?.witnesses?.map((witness, index) => (
                <div key={index} className="flex gap-2 mb-6">
                  <div className="flex-1">
                    <label
                      htmlFor="witness-name"
                      className="block text-xs font-medium mb-1"
                    >
                      Fullname
                    </label>
                    <input
                      id="witness-name"
                      type="text"
                      value={witness.name || ""}
                      onChange={(e) =>
                        handleWitnessChange(index, "name", e.target.value)
                      }
                      placeholder="Name"
                      className="custom-input text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="witness-label"
                      className="block text-xs font-medium mb-1"
                    >
                      Label
                    </label>
                    <input
                      id="witness-label"
                      type="text"
                      value={witness.label || ""}
                      onChange={(e) =>
                        handleWitnessChange(index, "label", e.target.value)
                      }
                      placeholder="Label (e.g., Witness, Affiant)"
                      className="custom-input text-sm flex-1"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => removeWitness(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ✕
                  </button>
                </div>
              ))}

              <div className="flex items-center justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={handleInsertSignatureBlock}
                  className="btn-action-white text-sm"
                >
                  Insert Signatories Block into Editor
                </button>
                <button
                  type="button"
                  onClick={addWitness}
                  className="btn-action text-sm"
                >
                  + Add Signatory
                </button>
              </div>
            </div>

            {/* Refresh Template Button */}
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={handleRefreshTemplate}
                className="btn-action text-sm flex items-center gap-2"
                title="Revert editor content with current default data"
              >
                Revert Changes
              </button>
            </div>
          </div>

          {/* Rich Text Editor */}
          <NotarialEditor
            content={formData.notarization?.content || defaultTemplate}
            onChange={handleContentChange}
            onReady={handleEditorReady}
            placeholder="Type or paste your notarization content here. Use the table button to insert affiant tables."
            navbarHeight={80}
          />

          {/* Notary Information */}
          <div>
            <h3 className="text-md font-semibold mb-2">Notary Information</h3>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label
                  htmlFor="doc-no"
                  className="block text-xs font-medium mb-1"
                >
                  Doc No.
                </label>
                <input
                  id="doc-no"
                  type="text"
                  value={formData.notarization?.notaryInfo?.docNo || ""}
                  onChange={(e) =>
                    handleNotaryInfoChange("docNo", e.target.value)
                  }
                  className="w-full custom-input px-4 py-2 border rounded"
                  placeholder="______"
                />
              </div>
              <div>
                <label
                  htmlFor="page-no"
                  className="block text-xs font-medium mb-1"
                >
                  Page No.
                </label>
                <input
                  id="page-no"
                  type="text"
                  value={formData.notarization?.notaryInfo?.pageNo || ""}
                  onChange={(e) =>
                    handleNotaryInfoChange("pageNo", e.target.value)
                  }
                  className="w-full custom-input px-4 py-2 border rounded"
                  placeholder="______"
                />
              </div>
              <div>
                <label
                  htmlFor="book-no"
                  className="block text-xs font-medium mb-1"
                >
                  Book No.
                </label>
                <input
                  id="book-no"
                  type="text"
                  value={formData.notarization?.notaryInfo?.bookNo || ""}
                  onChange={(e) =>
                    handleNotaryInfoChange("bookNo", e.target.value)
                  }
                  className="w-full custom-input px-4 py-2 border rounded"
                  placeholder="______"
                />
              </div>
              <div>
                <label
                  htmlFor="series"
                  className="block text-xs font-medium mb-1"
                >
                  Series
                </label>
                <input
                  id="series"
                  type="text"
                  value={formData.notarization?.notaryInfo?.series || ""}
                  onChange={(e) =>
                    handleNotaryInfoChange("series", e.target.value)
                  }
                  className="w-full custom-input px-4 py-2 border rounded"
                  placeholder="______"
                />
              </div>
            </div>
          </div>

          {/* Helper Text */}
          <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-3">
            <p className="text-xs text-gray-200">
              <strong>Note:</strong>
              <ul className="list-disc ml-5 mt-2">
                <li>
                  The "@page" annotation inside the editor is automatically
                  replaced in PDF generation
                </li>
                <li>
                  Insert a page break (horizontal line) to separate pages in
                  your document.
                </li>
                <li>
                  Formatting the table size in the editor might not reflect in
                  the generated PDF.
                </li>
              </ul>
            </p>
          </div>
        </div>
      </div>
    );
  }
);

NotarizationSection.displayName = "NotarizationSection";

export default NotarizationSection;
